import { useContext } from 'react';
import type { FunctionComponent } from 'react';
import { Navigate, Outlet } from 'react-router';
import { useFlags } from 'launchdarkly-react-client-sdk';

import { useGetOnboardingProgress } from 'api/onboarding/useGetOnboardingProgress';
import { useOnboardingFlow } from 'api/onboarding/useOnboardingFlow';
import { useOnboardingRestaurant } from 'api/onboarding/useOnboardingRestaurant';

import { HeadquarterContext } from 'context/HeadquarterContext';

import { STATUSES } from 'hooks/useLocation';

import { onboardingPath } from 'containers/OnboardingWizard/steps';

type HeadquarterLocation = {
  isLocationLoading: boolean;
  locationStatus?: string;
  location?: { id: string; is_live?: boolean };
};

/**
 * Sends a self-serve partner who still owes a required onboarding step to their restaurant's
 * wizard. Sales-led (`legacy`) restaurants onboard elsewhere and pass straight through, as does
 * anyone with no single restaurant to onboard, or anyone while a lookup or location is loading or
 * has failed. A live restaurant passes through too, since its location reports `is_live`.
 */
export const OnboardingLockIn: FunctionComponent = () => {
  const { selfServeOnboarding } = useFlags();
  // wraps every dashboard route, so nothing is looked up unless the wizard is on
  const { restaurantId } = useOnboardingRestaurant({ isEnabled: !!selfServeOnboarding });
  const wizardRestaurantId = selfServeOnboarding ? restaurantId : undefined;
  const { data: onboardingFlow } = useOnboardingFlow(wizardRestaurantId);
  const isSelfGuided = onboardingFlow === 'self_guided';
  // the headquarter loads the sole location's `is_live`, so a live restaurant never reads progress
  const headquarterContext = useContext(HeadquarterContext) as HeadquarterLocation | undefined;
  // after a failed lookup, the company list's copy of the location isn't a reliable `is_live`
  const isNotLive =
    !headquarterContext?.isLocationLoading &&
    headquarterContext?.locationStatus === STATUSES.LOADED &&
    headquarterContext?.location?.id === wizardRestaurantId &&
    headquarterContext?.location?.is_live === false;
  const { data: progress } = useGetOnboardingProgress(
    isSelfGuided && isNotLive ? wizardRestaurantId : undefined,
  );

  const owesRequiredStep = !!progress?.steps.required.some(step => step.status === 'incomplete');

  if (wizardRestaurantId && isSelfGuided && isNotLive && owesRequiredStep) {
    return <Navigate to={onboardingPath(wizardRestaurantId)} replace />;
  }

  return <Outlet />;
};
